import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../utils/api";

const initialState = {
  isLoading: false,
  error: null,
  userPackageDetails: localStorage.getItem('userPackageDetails') 
    ? JSON.parse(localStorage.getItem('userPackageDetails')) 
    : null,
  walletBalance: 0,
  currencyCode: "",
  currencySymbol: "",
  isBalanceLoaded: false,
  lowBalanceThreshold: null,
  hasCustomLowBalanceThreshold: false,
  isLowBalance: false,
  isNegativeBalance: false,
};

export const getUserPackageDetails = createAsyncThunk(
  "wallet/getUserPackageDetails",
  async (userId, { rejectWithValue }) => {
    try {
      const userToken = localStorage.getItem('token');
      const decodedToken = userToken ? JSON.parse(atob(userToken.split('.')[1])) : {};
      const id = userId || decodedToken?.userId || decodedToken?.id || decodedToken?.user_id;
      
      const url = id ? `/user/getUserWithPackageDetails?userId=${id}` : '/user/getUserWithPackageDetails';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching user package details:', error?.response?.data || error);
      return rejectWithValue(error?.response?.data || { message: 'Failed to fetch package details' });
    }
  }
);

export const getAllEntityWalletBalances = createAsyncThunk(
  "wallet/getAllEntityWalletBalances",
  async (queryParams = "", { rejectWithValue }) => {
    try {
      const response = await api.get(`/wallet/getAllEntityWalletBalances${queryParams}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || { message: 'Failed to fetch entity wallet balances' });
    }
  }
);

export const getWalletBalance = createAsyncThunk(
  "wallet/getWalletBalance",
  async (entityId = null, { rejectWithValue }) => {
    try {
      const url = entityId ? `/wallet/getWalletBalance?entityId=${entityId}` : '/wallet/getWalletBalance';
      const response = await api.get(url);
      if (response.data?.status === false || response.data?.data?.balance == null || !Number.isFinite(Number(response.data.data.balance))) {
        return rejectWithValue({ message: response.data?.message || 'Wallet balance is unavailable' });
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || { message: 'Failed to fetch wallet balance' });
    }
  }
);

const walletSlice = createSlice({
  name: "wallet",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUserPackageDetails.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserPackageDetails.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.userPackageDetails = payload.data;
        localStorage.setItem('userPackageDetails', JSON.stringify(payload.data));
      })
      .addCase(getUserPackageDetails.rejected, (state, { error }) => {
        state.isLoading = false;
        state.error = error.message;
      })
      .addCase(getWalletBalance.pending, (state) => {
        state.error = null;
        state.isBalanceLoaded = false;
      })
      .addCase(getWalletBalance.fulfilled, (state, { payload }) => {
        state.error = null;
        state.walletBalance = payload.data?.balance || 0;
        state.currencyCode =
          payload.data?.currency_code ||
          payload.data?.billing_currency_code ||
          payload.data?.display_currency ||
          "";
        state.currencySymbol = payload.data?.currency_symbol || "";
        state.lowBalanceThreshold = payload.data?.low_balance_threshold ?? null;
        state.hasCustomLowBalanceThreshold = Boolean(payload.data?.has_custom_low_balance_threshold);
        state.isLowBalance = Boolean(payload.data?.is_low_balance);
        state.isNegativeBalance = Boolean(payload.data?.is_negative_balance);
        state.isBalanceLoaded = true;
      })
      .addCase(getWalletBalance.rejected, (state, action) => {
        state.isBalanceLoaded = false;
        state.error = action.payload?.message || 'Wallet balance is unavailable';
      });
  },
});

export const walletReducer = walletSlice.reducer;
export default walletReducer;
